import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    EyeIcon,
} from "@heroicons/react/20/solid";
import { Heading, Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS } from "../data/events";
import { StatCard, Card, HBar, FeedEntry, CorrelationBadges, IncidentChain, SectionTitle } from "../shared";

/* ── Trust Score Gauge ────────────────────────────────────────────── */
function TrustScoreGauge({ value }: { value: number }) {
    const r = 60;
    const circ = 2 * Math.PI * r;
    const pct = value / 100;
    const offset = circ * (1 - pct * 0.75); // 270 degrees max
    const color = value >= 80 ? "#22c55e" : value >= 50 ? "#eab308" : "#ef4444";
    return (
        <div className="flex flex-col items-center">
            <svg viewBox="0 0 160 130" className="w-full max-w-[220px]">
                <defs>
                    <filter id="trustGlow">
                        <feGaussianBlur stdDeviation="4" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <linearGradient id="trustGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={color} />
                        <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                    </linearGradient>
                </defs>
                {/* background arc */}
                <circle
                    cx="80"
                    cy="80"
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    className="text-zinc-200 dark:text-zinc-700"
                    strokeWidth="10"
                    strokeDasharray={`${circ * 0.75} ${circ * 0.25}`}
                    transform="rotate(135 80 80)"
                    strokeLinecap="round"
                />
                {/* value arc */}
                <circle
                    cx="80"
                    cy="80"
                    r={r}
                    fill="none"
                    stroke="url(#trustGrad)"
                    strokeWidth="10"
                    strokeDasharray={`${circ * 0.75 * pct} ${circ}`}
                    transform="rotate(135 80 80)"
                    strokeLinecap="round"
                    filter="url(#trustGlow)"
                />
                {/* center text */}
                <text
                    x="80"
                    y="78"
                    textAnchor="middle"
                    className="fill-zinc-700 dark:fill-zinc-200"
                    fontSize="28"
                    fontWeight="900"
                >
                    {value}
                </text>
                <text x="80" y="96" textAnchor="middle" className="fill-zinc-400" fontSize="9" fontWeight="600">
                    Avg Trust Score
                </text>
                {/* min/max labels */}
                <text x="28" y="125" textAnchor="middle" className="fill-zinc-400" fontSize="8">
                    0
                </text>
                <text x="132" y="125" textAnchor="middle" className="fill-zinc-400" fontSize="8">
                    100
                </text>
            </svg>
        </div>
    );
}

/* ── Device Compliance Matrix ─────────────────────────────────────── */
function DeviceComplianceMatrix() {
    const devices = [
        { type: "Laptops", compliant: 142, nonCompliant: 8, pending: 3 },
        { type: "Desktops", compliant: 67, nonCompliant: 5, pending: 2 },
        { type: "Mobile", compliant: 48, nonCompliant: 12, pending: 6 },
        { type: "Tablets", compliant: 15, nonCompliant: 3, pending: 1 },
    ];
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr className="border-b border-zinc-200/50 dark:border-zinc-700/50">
                        <th className="py-2 text-left font-semibold text-zinc-500">Device Type</th>
                        <th className="py-2 text-center font-semibold text-emerald-500">Compliant</th>
                        <th className="py-2 text-center font-semibold text-red-500">Non-Compliant</th>
                        <th className="py-2 text-center font-semibold text-amber-500">Pending</th>
                    </tr>
                </thead>
                <tbody>
                    {devices.map((d) => {
                        const total = d.compliant + d.nonCompliant + d.pending;
                        return (
                            <tr
                                key={d.type}
                                className="border-b border-zinc-100/50 transition-colors hover:bg-blue-50/30 dark:border-zinc-800/50 dark:hover:bg-blue-950/10"
                            >
                                <td className="py-2.5 font-medium text-zinc-700 dark:text-zinc-300">{d.type}</td>
                                <td className="py-2.5 text-center">
                                    <span className="inline-flex items-center gap-1">
                                        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                            {d.compliant}
                                        </span>
                                        <span className="text-[9px] text-zinc-400">
                                            ({Math.round((d.compliant / total) * 100)}%)
                                        </span>
                                    </span>
                                </td>
                                <td className="py-2.5 text-center">
                                    <span className="inline-flex items-center gap-1">
                                        <span className="font-mono font-bold text-red-600 dark:text-red-400">
                                            {d.nonCompliant}
                                        </span>
                                        <span className="text-[9px] text-zinc-400">
                                            ({Math.round((d.nonCompliant / total) * 100)}%)
                                        </span>
                                    </span>
                                </td>
                                <td className="py-2.5 text-center">
                                    <span className="inline-flex items-center gap-1">
                                        <span className="font-mono font-bold text-amber-600 dark:text-amber-400">
                                            {d.pending}
                                        </span>
                                        <span className="text-[9px] text-zinc-400">
                                            ({Math.round((d.pending / total) * 100)}%)
                                        </span>
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* ── Session Map (Germany dots) ───────────────────────────────────── */
// Germany outline path (source: sirLisko/world-map-country-shapes, MIT license, viewBox 0 0 48 51)
const GERMANY_PATH =
    "M40.1 9.3L37.7 7.4L32.2 5.0L29.7 6.7L25.0 7.8L24.9 5.7L20.0 4.3L19.8 2.0L16.8 2.9L13.2 2.1L13.6 5.5L14.8 7.7L11.8 10.7L10.8 9.4L6.9 9.7L6.0 11.0L7.0 13.0L6.0 18.6L4.9 20.9L2.0 20.9L3.1 27.3L2.7 31.5L3.7 32.9L3.5 35.6L5.9 37.2L13.0 38.4L10.7 42.6L10.2 47.1L14.4 47.1L15.4 45.7L20.8 47.6L22.3 47.3L24.9 49.0L25.5 47.4L29.9 47.7L33.3 46.5L35.7 46.7L37.4 48.0L37.8 46.9L36.8 42.9L38.5 42.1L40.0 39.2L37.1 36.6L34.5 35.1L33.8 32.5L32.8 30.6L36.2 29.3L37.9 27.8L41.3 26.6L42.4 25.4L43.8 26.1L45.9 25.5L43.6 21.6L43.7 19.5L42.3 16.2L40.3 14.0L41.5 12.4L40.1 9.3Z";

function SessionMap() {
    const locations = [
        { name: "Munich", x: 265, y: 215, sessions: 98 },
        { name: "Berlin", x: 285, y: 82, sessions: 67 },
        { name: "Hamburg", x: 253, y: 68, sessions: 45 },
        { name: "Frankfurt", x: 237, y: 140, sessions: 38 },
        { name: "Cologne", x: 221, y: 125, sessions: 22 },
    ];
    return (
        <svg viewBox="100 40 280 230" className="mx-auto w-full max-w-[300px]">
            <defs>
                <radialGradient id="sessionDot">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                </radialGradient>
                <filter id="mapGlow">
                    <feGaussianBlur stdDeviation="2" />
                    <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>
            {/* Germany outline */}
            <g transform="translate(169, 46) scale(4.0, 3.595)">
                <path
                    d={GERMANY_PATH}
                    fill="rgba(59,130,246,0.03)"
                    stroke="rgba(59,130,246,0.15)"
                    strokeWidth={0.4}
                    className="dark:stroke-blue-800/30"
                />
            </g>
            {locations.map((loc) => (
                <g key={loc.name}>
                    <circle cx={loc.x} cy={loc.y} r={Math.max(loc.sessions / 5, 8)} fill="url(#sessionDot)" />
                    <circle cx={loc.x} cy={loc.y} r="4" fill="#22c55e" filter="url(#mapGlow)" />
                    <text
                        x={loc.x + 8}
                        y={loc.y - 6}
                        className="fill-zinc-600 dark:fill-zinc-300"
                        fontSize="8"
                        fontWeight="700"
                    >
                        {loc.name}
                    </text>
                    <text x={loc.x + 8} y={loc.y + 4} className="fill-zinc-400" fontSize="7">
                        {loc.sessions} sessions
                    </text>
                    {/* Pulse animation */}
                    <circle cx={loc.x} cy={loc.y} r="4" fill="none" stroke="#22c55e" strokeWidth="1" opacity="0.5">
                        <animate
                            attributeName="r"
                            from="4"
                            to={String(Math.max(loc.sessions / 5, 8))}
                            dur="3s"
                            repeatCount="indefinite"
                        />
                        <animate attributeName="opacity" from="0.5" to="0" dur="3s" repeatCount="indefinite" />
                    </circle>
                </g>
            ))}
        </svg>
    );
}

/* ── Access Policy Timeline ───────────────────────────────────────── */
function PolicyTimeline() {
    const events = [
        { day: "Mon", desc: "MFA enforced for VPN users", type: "added", color: "bg-emerald-400" },
        { day: "Tue", desc: "Device compliance check updated", type: "updated", color: "bg-blue-400" },
        { day: "Wed", desc: "Guest network policy restricted", type: "updated", color: "bg-blue-400" },
        { day: "Thu", desc: "Geo-blocking: RU, CN added", type: "added", color: "bg-emerald-400" },
        { day: "Fri", desc: "IoT segment isolated", type: "added", color: "bg-emerald-400" },
        { day: "Sat", desc: "Weekend access policy active", type: "scheduled", color: "bg-purple-400" },
        { day: "Sun", desc: "Maintenance window exception", type: "temporary", color: "bg-amber-400" },
    ];
    return (
        <div className="relative pl-6">
            <div className="absolute left-2.5 top-0 h-full w-px bg-gradient-to-b from-blue-500/30 via-purple-500/20 to-transparent" />
            <div className="space-y-3">
                {events.map((e) => (
                    <div key={e.day} className="relative flex items-start gap-3">
                        <div
                            className={`absolute left-[-18px] top-1.5 h-2.5 w-2.5 rounded-full ${e.color} shadow-sm`}
                        />
                        <div className="min-w-0 flex-1 rounded-lg border border-zinc-200/30 bg-white/30 px-3 py-2 backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/30">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                                    {e.day}
                                </span>
                                <Badge
                                    color={
                                        e.type === "added"
                                            ? "green"
                                            : e.type === "updated"
                                              ? "blue"
                                              : e.type === "scheduled"
                                                ? "purple"
                                                : "amber"
                                    }
                                >
                                    {e.type}
                                </Badge>
                            </div>
                            <p className="mt-0.5 text-xs text-zinc-700 dark:text-zinc-300">{e.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Application Access Heatmap ───────────────────────────────────── */
function AppAccessHeatmap() {
    const apps = ["SAP", "Office 365", "Jira", "GitLab", "Salesforce"];
    const groups = ["Engineering", "Sales", "HR", "Finance", "IT"];
    const data: number[][] = [
        [85, 12, 45, 78, 92],
        [95, 88, 72, 65, 91],
        [42, 35, 89, 18, 78],
        [38, 28, 55, 95, 68],
        [72, 67, 34, 42, 88],
    ];
    const maxVal = 95;
    return (
        <div className="overflow-x-auto">
            <div className="inline-block min-w-[400px]">
                <div className="mb-1 ml-20 flex">
                    {groups.map((g) => (
                        <div key={g} className="w-16 text-center text-[8px] font-semibold text-zinc-500">
                            {g}
                        </div>
                    ))}
                </div>
                {apps.map((app, ai) => (
                    <div key={app} className="flex items-center">
                        <span className="w-20 pr-2 text-right text-[9px] font-medium text-zinc-500">{app}</span>
                        {data[ai].map((v, gi) => {
                            const intensity = v / maxVal;
                            return (
                                <div
                                    key={gi}
                                    className="flex h-8 w-16 items-center justify-center rounded-md border border-white/10 text-[9px] font-bold transition-all hover:z-10 hover:scale-110 dark:border-zinc-900/30"
                                    style={{
                                        backgroundColor: `rgba(34,197,94,${0.1 + intensity * 0.7})`,
                                        color: intensity > 0.5 ? "white" : "inherit",
                                    }}
                                    title={`${app} × ${groups[gi]}: ${v} sessions`}
                                >
                                    {v}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Endpoint Health Mini-Gauges ──────────────────────────────────── */
function EndpointHealth() {
    const metrics = [
        { label: "Patched", value: 94, color: "#22c55e" },
        { label: "Encrypted", value: 99, color: "#3b82f6" },
        { label: "Compliant", value: 91, color: "#8b5cf6" },
        { label: "Healthy", value: 96, color: "#06b6d4" },
    ];
    return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {metrics.map((m) => {
                const r = 30;
                const circ = 2 * Math.PI * r;
                const offset = circ * (1 - m.value / 100);
                return (
                    <div
                        key={m.label}
                        className="flex flex-col items-center rounded-xl border border-zinc-200/30 bg-white/40 p-3 backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/40"
                    >
                        <svg viewBox="0 0 80 80" className="h-16 w-16">
                            <circle
                                cx="40"
                                cy="40"
                                r={r}
                                fill="none"
                                stroke="currentColor"
                                className="text-zinc-200 dark:text-zinc-700"
                                strokeWidth="5"
                            />
                            <circle
                                cx="40"
                                cy="40"
                                r={r}
                                fill="none"
                                stroke={m.color}
                                strokeWidth="5"
                                strokeLinecap="round"
                                strokeDasharray={circ}
                                strokeDashoffset={offset}
                                transform="rotate(-90 40 40)"
                                style={{ filter: `drop-shadow(0 0 4px ${m.color}40)` }}
                            />
                            <text
                                x="40"
                                y="43"
                                textAnchor="middle"
                                className="fill-zinc-700 dark:fill-zinc-200"
                                fontSize="14"
                                fontWeight="800"
                            >
                                {m.value}%
                            </text>
                        </svg>
                        <p className="mt-1 text-[10px] font-semibold text-zinc-500">{m.label}</p>
                    </div>
                );
            })}
        </div>
    );
}

/* ── Main Dashboard ───────────────────────────────────────────────── */
export function ZtnaDashboard({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
}) {
    const { t } = useTranslation();
    const ztnaEvents = UNIFIED_EVENTS.filter((e) => e.system === "ztna");
    const statusMap: Record<string, "connected" | "limited" | "denied"> = {
        "ZTNA-091": "denied",
        "ZTNA-089": "limited",
        "ZTNA-088": "limited",
        "ZTNA-085": "connected",
        "ZTNA-082": "connected",
    };
    const statusColor = { connected: "green", limited: "amber", denied: "red" } as const;
    const userMap: Record<string, string> = {
        "ZTNA-091": "s.braun",
        "ZTNA-089": "3 users",
        "ZTNA-088": "j.mueller",
        "ZTNA-085": "a.weber",
        "ZTNA-082": "policy",
    };
    const scoreMap: Record<string, number> = {
        "ZTNA-091": 23,
        "ZTNA-089": 58,
        "ZTNA-088": 64,
        "ZTNA-085": 87,
        "ZTNA-082": 91,
    };
    const deviceMap: Record<string, string> = {
        "ZTNA-091": "Unknown Device",
        "ZTNA-089": "Various",
        "ZTNA-088": "iPhone 15",
        "ZTNA-085": "ThinkPad X1",
        "ZTNA-082": "System",
    };
    function scoreColor(s: number) {
        return s >= 80
            ? "text-zinc-900 dark:text-zinc-100"
            : s >= 50
              ? "text-zinc-600 dark:text-zinc-400"
              : "text-zinc-400 dark:text-zinc-500";
    }

    return (
        <>
            <Heading>{t("ztna.title")}</Heading>

            {/* ── Stat Cards ──────────────────────────────────────────── */}
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label={t("ztna.activeSessions")} value="284" />
                <StatCard label={t("ztna.policies")} value="47" />
                <StatCard label={t("ztna.avgTrustScore")} value="81.4" />
                <StatCard label={t("ztna.deniedRequests")} value="23" trend="+8 vs. yesterday" />
            </div>

            {/* ── Original 3-col grid ─────────────────────────────────── */}
            <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <Card>
                    <Subheading>Trust Score Distribution</Subheading>
                    <div className="mt-4">
                        <HBar
                            items={[
                                {
                                    label: "90\u2013100 (Trusted)",
                                    value: 142,
                                    max: 142,
                                    color: "bg-blue-700 dark:bg-blue-300",
                                },
                                {
                                    label: "70\u201389 (Normal)",
                                    value: 98,
                                    max: 142,
                                    color: "bg-blue-700 dark:bg-blue-300",
                                },
                                { label: "50\u201369 (Elevated)", value: 31, max: 142, color: "bg-blue-400" },
                                {
                                    label: "0\u201349 (Untrusted)",
                                    value: 13,
                                    max: 142,
                                    color: "bg-blue-700 dark:bg-blue-400",
                                },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Sessions by Location</Subheading>
                    <div className="mt-4">
                        <HBar
                            items={[
                                { label: "Munich", value: 98, max: 98, color: "bg-blue-700 dark:bg-blue-300" },
                                { label: "Berlin", value: 67, max: 98, color: "bg-blue-500" },
                                { label: "Hamburg", value: 45, max: 98, color: "bg-blue-400" },
                                { label: "Frankfurt", value: 38, max: 98, color: "bg-blue-300 dark:bg-blue-600" },
                                { label: "Other", value: 36, max: 98, color: "bg-blue-400" },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Cross-System Feed</Subheading>
                    <div className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
                        <FeedEntry
                            time="14:21"
                            icon={SignalIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="ndr"
                            onNavigate={onNavigate}
                        >
                            s.braun denied — NDR flagged C2 traffic
                        </FeedEntry>
                        <FeedEntry
                            time="13:58"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="mail"
                            onNavigate={onNavigate}
                        >
                            3 users received phishing — Mail blocked
                        </FeedEntry>
                        <FeedEntry
                            time="13:55"
                            icon={ShieldCheckIcon}
                            color="bg-blue-500"
                            linkedSystem="soc"
                            onNavigate={onNavigate}
                        >
                            j.mueller trust → 64, SOC investigating
                        </FeedEntry>
                        <FeedEntry
                            time="13:41"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="phishing"
                            onNavigate={onNavigate}
                        >
                            a.weber clicked phishing sim — trust impacted
                        </FeedEntry>
                        <FeedEntry
                            time="11:30"
                            icon={BugAntIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="pentests"
                            onNavigate={onNavigate}
                        >
                            PT-003 MFA — hardware tokens enforced
                        </FeedEntry>
                    </div>
                </Card>
            </div>

            {/* ── Trust Score Gauge + Session Map ─────────────────────── */}
            <SectionTitle title="Trust & Sessions" description="Average trust score and active session locations" />
            <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Trust Score Gauge</Subheading>
                    <div className="mt-4">
                        <TrustScoreGauge value={81} />
                    </div>
                </Card>
                <Card>
                    <Subheading>Active Sessions Map</Subheading>
                    <div className="mt-4">
                        <SessionMap />
                    </div>
                </Card>
            </div>

            {/* ── Device Compliance ────────────────────────────────────── */}
            <SectionTitle title="Device Compliance" description="Compliance status by device type" />
            <Card>
                <DeviceComplianceMatrix />
            </Card>

            {/* ── Access Policy Timeline ──────────────────────────────── */}
            <SectionTitle title="Access Policy Timeline" description="Policy changes in the last 7 days" />
            <Card>
                <PolicyTimeline />
            </Card>

            {/* ── Application Access Heatmap ──────────────────────────── */}
            <SectionTitle
                title="Application Access Heatmap"
                description="Access frequency by application and user group"
            />
            <Card>
                <AppAccessHeatmap />
            </Card>

            {/* ── Endpoint Health ──────────────────────────────────────── */}
            <SectionTitle title="Endpoint Health" description="Device security posture overview" />
            <Card>
                <EndpointHealth />
            </Card>

            {/* ── Cross-System Incident Chains (original) ─────────────── */}
            <Card className="mt-6">
                <Subheading>Cross-System Incident Chains</Subheading>
                <div className="mt-4 space-y-3">
                    <div>
                        <Text className="mb-1 text-xs font-medium sm:text-xs">ACCESS-091: Compromised Session</Text>
                        <IncidentChain
                            onNavigate={onNavigate}
                            steps={[
                                { system: "ndr", label: "Anomalous traffic" },
                                { system: "ztna", label: "Trust \u2192 23" },
                                { system: "soc", label: "Alert" },
                                { system: "mail", label: "Quarantined" },
                            ]}
                        />
                    </div>
                </div>
            </Card>

            {/* ── Sessions Table (original) ───────────────────────────── */}
            <Subheading className="mt-10">{t("ztna.sessions")}</Subheading>
            <Table className="mt-4" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("ztna.user")}</TableHeader>
                        <TableHeader>{t("ztna.device")}</TableHeader>
                        <TableHeader>{t("ztna.trustScore")}</TableHeader>
                        <TableHeader>{t("ztna.status")}</TableHeader>
                        <TableHeader>Correlated</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ztnaEvents.map((e) => (
                        <TableRow key={e.id}>
                            <TableCell className="font-mono">{userMap[e.id] ?? "—"}</TableCell>
                            <TableCell>{deviceMap[e.id] ?? "—"}</TableCell>
                            <TableCell className={`font-mono font-bold ${scoreColor(scoreMap[e.id] ?? 0)}`}>
                                {scoreMap[e.id] ?? "—"}
                            </TableCell>
                            <TableCell>
                                <Badge color={statusColor[statusMap[e.id] ?? "connected"]}>
                                    {t(`ztna.${statusMap[e.id] ?? "connected"}`)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <CorrelationBadges systems={e.correlated} onNavigate={onNavigate} />
                            </TableCell>
                            <TableCell>
                                <button onClick={() => onInvestigate(e)} className="text-zinc-400 hover:text-blue-600">
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}
